<template>
    <div class="tl-page">
        <!-- Header -->
        <div class="tl-header">
            <div class="tl-header-left">
                <router-link to="/assets" class="tl-back-btn"><i class="fas fa-arrow-left"></i></router-link>
                <div class="tl-icon-wrap"><i class="fas fa-history"></i></div>
                <div>
                    <h2 class="tl-title">Asset Timeline</h2>
                    <span class="tl-subtitle" v-if="asset">{{ asset.name }}</span>
                </div>
                <span class="tl-count" v-if="assetLogs.length">{{ assetLogs.length }} events</span>
            </div>
        </div>

        <!-- Asset Summary -->
        <div class="tl-summary" v-if="asset">
            <div class="tl-sum-card">
                <div class="tl-sum-icon tl-sum-blue"><i class="fas fa-box"></i></div>
                <div class="tl-sum-info">
                    <span class="tl-sum-label">Asset Name</span>
                    <span class="tl-sum-val">{{ asset.name }}</span>
                </div>
            </div>
            <div class="tl-sum-card" v-if="asset.price">
                <div class="tl-sum-icon tl-sum-teal"><i class="fas fa-rupee-sign"></i></div>
                <div class="tl-sum-info">
                    <span class="tl-sum-label">Price</span>
                    <span class="tl-sum-val">&#8377;{{ Number(asset.price).toLocaleString('en-IN') }}</span>
                </div>
            </div>
            <div class="tl-sum-card" v-if="asset.serialNo">
                <div class="tl-sum-icon tl-sum-purple"><i class="fas fa-barcode"></i></div>
                <div class="tl-sum-info">
                    <span class="tl-sum-label">Serial No</span>
                    <span class="tl-sum-val">{{ asset.serialNo }}</span>
                </div>
            </div>
            <div class="tl-sum-card">
                <div class="tl-sum-icon tl-sum-amber"><i class="fas fa-calendar-plus"></i></div>
                <div class="tl-sum-info">
                    <span class="tl-sum-label">Created</span>
                    <span class="tl-sum-val">{{ formatIndianDate(asset.createdAt) }}</span>
                </div>
            </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="tl-loading"><div class="tl-spinner"></div><span>Loading timeline...</span></div>

        <!-- Empty -->
        <div v-else-if="!assetLogs.length" class="tl-empty">
            <div class="tl-empty-icon"><i class="fas fa-history"></i></div>
            <h3>No logs found for this asset</h3>
        </div>

        <!-- Timeline Chain -->
        <div v-else class="tl-chain">
            <AssetLogChronologicalViewItem
                v-for="(assetLog, idx) in assetLogs"
                :key="assetLog.id"
                :assetLog="assetLog"
                :index="idx"
                :isLast="idx === assetLogs.length - 1"
            />
        </div>
    </div>
</template>

<script>
import { formatIndianDate } from "../../utils/common"
import { http } from "../../utils/http"
import AssetLogChronologicalViewItem from "./asset-log-chronological-view-item.vue"

export default {
    data() {
        return { loading: true, assetLogs: [], asset: null }
    },
    components: { AssetLogChronologicalViewItem },
    mounted() { this.fetchAssetLogs() },
    methods: {
        formatIndianDate,
        async fetchAssetLogs() {
            this.loading = true
            try {
                const res = await http.get(`/asset-logs?asset=${this.$route.params.id}`)
                this.asset = res.data.asset
                this.assetLogs = res.data.result
            } catch (err) { console.log("error in fetching logs") }
            this.loading = false
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";
.tl-page { animation: tlFade .3s ease; }
@keyframes tlFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }

.tl-header { display:flex; align-items:center; justify-content:space-between; gap:12px; background:white; border:1px solid $gray100; border-radius:8px; padding:10px 16px; margin-bottom:12px; box-shadow:$shadowSm; flex-wrap:wrap; }
.tl-header-left { display:flex; align-items:center; gap:10px; }
.tl-back-btn { width:32px; height:32px; border-radius:8px; border:1px solid $gray200; display:flex; align-items:center; justify-content:center; color:$gray500; font-size:13px; transition:all $transitionFast; text-decoration:none; &:hover { background:$gray50; color:$primaryColor; border-color:$primaryColor; } }
.tl-icon-wrap { width:32px; height:32px; border-radius:8px; background:linear-gradient(135deg,$primaryLight,darken($primaryLight,4%)); color:$primaryColor; display:flex; align-items:center; justify-content:center; font-size:14px; }
.tl-title { font-size:15px; font-weight:700; color:$gray800; margin:0; line-height:1.2; }
.tl-subtitle { font-size:11.5px; color:$gray400; font-weight:500; }
.tl-count { font-size:11px; font-weight:600; padding:3px 10px; border-radius:$radiusFull; background:$gray50; border:1px solid $gray100; color:$gray500; }

.tl-summary { display:grid; grid-template-columns:repeat(4,1fr); gap:10px; margin-bottom:16px; }
.tl-sum-card { background:white; border:1px solid $gray100; border-radius:8px; padding:12px 14px; box-shadow:$shadowSm; display:flex; align-items:center; gap:10px; }
.tl-sum-icon { width:34px; height:34px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
.tl-sum-blue { background:#eff6ff; color:#2563eb; }
.tl-sum-purple { background:#f5f3ff; color:#7c3aed; }
.tl-sum-teal { background:#f0fdfa; color:#0d9488; }
.tl-sum-amber { background:#fffbeb; color:#d97706; }
.tl-sum-info { display:flex; flex-direction:column; min-width:0; }
.tl-sum-label { font-size:10px; color:$gray400; font-weight:500; text-transform:uppercase; letter-spacing:0.3px; }
.tl-sum-val { font-size:12.5px; color:$gray800; font-weight:700; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

.tl-loading { display:flex; align-items:center; justify-content:center; gap:10px; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; span { font-size:13px; color:$gray400; } }
.tl-spinner { width:24px; height:24px; border:2.5px solid $gray200; border-top-color:$primaryColor; border-radius:50%; animation:tlSpin .7s linear infinite; }
@keyframes tlSpin { to { transform:rotate(360deg); } }
.tl-empty { text-align:center; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; h3 { font-size:14px; font-weight:700; color:$gray600; margin:0; } }
.tl-empty-icon { width:48px; height:48px; border-radius:$radiusFull; background:$gray50; border:1px solid $gray100; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; i { font-size:20px; color:$gray300; } }

.tl-chain { position:relative; padding-left:32px; }

@media(max-width:992px) { .tl-summary { grid-template-columns:repeat(2,1fr); } }
@media(max-width:480px) { .tl-summary { grid-template-columns:1fr; } .tl-chain { padding-left:24px; } }
</style>
